import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { partnerReferencesApi } from '../../api/partner-governance-references-actions';
import { ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

const VERIFICATION_METHOD_LABELS: Record<string, string> = {
  email: 'Email', telephone: 'Telephone', physical_verification: 'Physical Verification', registry: 'Registry',
  donor_reference: 'Donor Reference', un_partner_portal: 'UN Partner Portal', other: 'Other',
};

export function PartnerReferencesTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const referencesQuery = useQuery({ queryKey: ['partner-references', partnerId], queryFn: () => partnerReferencesApi.list(partnerId) });
  const { register, handleSubmit, reset } = useForm<{ referenceOrganization: string; contactName: string; contactEmail: string; relationship: string; projectName: string; verificationMethod: string }>();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['partner-references', partnerId] });
  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => partnerReferencesApi.create(partnerId, values),
    onSuccess: () => { showSuccess('Reference added.'); invalidate(); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add reference.'),
  });
  const outcomeMutation = useMutation({
    mutationFn: ({ id, outcome }: { id: number; outcome: string }) => partnerReferencesApi.setOutcome(id, outcome),
    onSuccess: () => { showSuccess('Outcome recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="panel">
      <div className="panel-body">
        {referencesQuery.data && (
          <DataTable
            rows={referencesQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No references on file yet."
            columns={[
              { header: 'Organization', render: (r) => r.reference_organization },
              { header: 'Contact', render: (r) => r.contact_name ?? '-' },
              { header: 'Relationship', render: (r) => r.relationship ?? '-' },
              { header: 'Verification Method', render: (r) => r.verification_method ? VERIFICATION_METHOD_LABELS[r.verification_method] : '-' },
              {
                header: 'Outcome', render: (r) => (
                  <select value={r.outcome} onChange={(e) => outcomeMutation.mutate({ id: r.id, outcome: e.target.value })}>
                    <option value="pending">Pending</option><option value="positive">Positive</option><option value="mixed">Mixed</option><option value="negative">Negative</option>
                  </select>
                ),
              },
            ]}
          />
        )}

        <h3>Add Reference</h3>
        <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
          <label>Reference Organization<input {...register('referenceOrganization', { required: true })} /></label>
          <label>Contact Name<input {...register('contactName')} /></label>
          <label>Contact Email<input type="email" {...register('contactEmail')} /></label>
          <label>Relationship<input {...register('relationship')} /></label>
          <label>Project<input {...register('projectName')} /></label>
          <label>
            Verification Method
            <select {...register('verificationMethod')}>
              <option value="">- choose -</option>
              {Object.entries(VERIFICATION_METHOD_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Adding…' : 'Add'}</button>
        </form>
      </div>
    </div>
  );
}
